import React, { useState } from 'react';
import Eye from 'lucide-react/dist/esm/icons/eye'; // Changed from { Eye }
import { ColorScheme } from '../ColorSchemeEditor/types';
import { useTheme } from '../../context/ThemeContext';
import { applyColorScheme } from '../../services/themeService';

interface ColorSchemesPageProps {
  schemes: { name: string; scheme: ColorScheme }[];
  currentScheme: ColorScheme | null;
  onApplyScheme: (scheme: ColorScheme) => void;
  onPreviewScheme?: (scheme: ColorScheme) => void;
}

export const ColorSchemesPage: React.FC<ColorSchemesPageProps> = ({ 
  schemes, 
  currentScheme, 
  onApplyScheme,
  onPreviewScheme 
}) => {
  const { theme } = useTheme();
  const [filter, setFilter] = useState('');

  // Filter schemes by name or description
  const filteredSchemes = filter.trim() === '' 
    ? schemes 
    : schemes.filter(({ name, scheme }) => 
        name.toLowerCase().includes(filter.toLowerCase()) || 
        scheme.description?.toLowerCase().includes(filter.toLowerCase())
      );

  const handleTestScheme = (scheme: ColorScheme) => {
    if (onPreviewScheme) {
      onPreviewScheme(scheme);
    } else {
      // Fallback to direct application
      applyColorScheme(scheme, theme);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-2">
          Predefined Color Schemes
        </h3>
        <p className="text-sm text-foreground-muted">
          Choose from our curated collection of beautiful color schemes.
        </p>
        
        {/* Filter input */}
        <div className="mt-4">
          <input 
            type="text"
            placeholder="Search schemes..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground text-sm"
          />
        </div>
      </div>
      
      {/* Schemes grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredSchemes.map(({ name, scheme }) => (
          <div
            key={scheme.id}            className={`p-4 border rounded-lg transition-all hover:shadow-md ${
              currentScheme?.id === scheme.id
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <div className="space-y-3">
              <div>
                <h4 className="font-medium text-foreground">{scheme.name}</h4>
                <p className="text-xs text-foreground-muted">{scheme.description}</p>
              </div>
              
              {/* Color Preview */}
              <div className="flex space-x-1">
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: `hsl(${scheme.colors.light['--primary']})` }}
                  title="Primary"
                />
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: `hsl(${scheme.colors.light['--secondary']})` }}
                  title="Secondary"
                />
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: `hsl(${scheme.colors.light['--accent']})` }}
                  title="Accent"
                />
                <div 
                  className="w-6 h-6 rounded border"
                  style={{ backgroundColor: `hsl(${scheme.colors.light['--background']})` }}
                  title="Background"
                />
              </div>              
              <div className="flex space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTestScheme(scheme);
                  }}
                  className="flex-1 px-3 py-2 bg-secondary text-foreground rounded-md hover:bg-secondary/90 transition-colors text-sm"
                >
                  <Eye className="h-4 w-4 mr-1 inline" />
                  Preview
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onApplyScheme(scheme);
                  }}
                  className="flex-1 px-3 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {filteredSchemes.length === 0 && (
          <div className="col-span-full p-8 text-center text-foreground-muted">
            No color schemes found matching "{filter}".
          </div>
        )}
      </div>
    </div>
  );
};